<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';
import { Bubble, BubbleContent } from 'next/polaris/ui/bubble';
import { polarisBubbleVariant } from 'next/polaris/skin/messageBubbleVariant';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

// The classes a bubble gets from the bubble that wraps it (padding, a data
// attribute) belong on the element that carries the color: the root in the old
// look, the Polaris BubbleContent in the new one.
defineOptions({ inheritAttrs: false });

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  sender,
  senderType,
} = useMessageContext();
const { t } = useI18n();

// null keeps the old look: the message (email, activity) is not a bubble in
// the kit.
const polarisVariant = computed(() =>
  polarisBubbleVariant({
    variant: variant.value,
    senderType: sender.value?.type ?? senderType.value,
  })
);

// The app's prose styles paint text slate-12, which disappears on the carbon
// agent bubble and in dark mode: inside a Polaris bubble the text takes the
// bubble's own foreground.
const POLARIS_CONTENT_CLASS =
  'p-0 type-polaris-body [&_.prose-bubble]:[color:inherit] [&_.prose-bubble_*]:[color:inherit]';

const polarisBubbleClass = computed(() => [
  'max-w-lg',
  {
    '[&>[data-slot=bubble-content]]:border-dashed':
      polarisVariant.value === 'outline',
    '[&_.prosemirror-mention-node]:font-semibold':
      polarisVariant.value === 'note',
  },
]);

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <Bubble
    v-if="polarisVariant"
    :variant="polarisVariant"
    :align="orientation === ORIENTATION.RIGHT ? 'end' : 'start'"
    :class="polarisBubbleClass"
  >
    <BubbleContent
      v-bind="{ ...$attrs, class: undefined }"
      :class="[POLARIS_CONTENT_CLASS, $attrs.class]"
    >
      <div
        v-if="inReplyTo"
        class="p-2 -mx-1 mb-2 rounded-polaris-md cursor-pointer bg-[color:color-mix(in_oklch,currentColor_8%,transparent)]"
        @click="scrollToMessage"
      >
        <div
          v-dompurify-html="replyToPreview"
          class="prose prose-bubble line-clamp-2"
        />
      </div>
      <slot />
      <MessageMeta
        v-if="shouldShowMeta"
        :class="flexOrientationClass"
        class="mt-2 opacity-70"
      />
    </BubbleContent>
  </Bubble>
  <div
    v-else
    v-bind="$attrs"
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2"
      />
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        {
          'px-4 pb-4 text-polaris-muted-foreground':
            variant === MESSAGE_VARIANTS.EMAIL,
          'text-n-amber-12/50': variant === MESSAGE_VARIANTS.PRIVATE,
          'text-n-slate-11':
            variant !== MESSAGE_VARIANTS.EMAIL &&
            variant !== MESSAGE_VARIANTS.PRIVATE,
        },
      ]"
      class="mt-2"
    />
  </div>
</template>
